import React, { useState, useCallback } from 'react';
import {
  Box,
  VStack,
  Heading,
  Button,
  Flex,
  StackDivider,
  HStack,
  Spinner,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import {
  SETTLEMENT_SERVICE_DASHBOARD_STRINGS,
  SSD_LEDGERS_DEFAULT_FILTERS,
  TAB_STATES
} from '@views/SettlementServiceDashboard/constant';
import { useDispatch, useSelector } from 'react-redux';
import { MdAdd } from 'react-icons/md';
import SearchBox from '@components/SearchBox/SearchBox';
import Pagination from '@components/Pagination';
import useDidMountEffect from '@hooks/useDidMount';
import { getAllLedgers } from '@store/actions/settlementServiceDashboardActions';
import { updateSettlementServiceLedgersReqData } from '@store/actions/reqDataActions';
import useDebounce from '@hooks/useDebounce';
import { CARD_TYPE } from '@constants';
import GenericCard from '@components/GenericCard';
import ModalLayout from '@components/Modal/ModalLayout';
import DynamicInputModal from '@components/DynamicInputModal';
import { ledgerSettlementsConfig as config } from '@views/SettlementServiceDashboard/utils';
import FilterSelect from '@components/FiltersBox/FilterSelect';

const LedgerSettlements = ({ selectedLedger, setSelectedLedger }) => {
  const dispatch = useDispatch();

  const reqData = useSelector(
    (state) => state.reqData.settlementServiceDashboard.ledgers
  );

  const { filters, searches, totalPages, pageNumber } = reqData;

  const {
    data: ledgerSettlements,
    isLoading,
    error
  } = useSelector((state) => state.settlementServiceDashboard.ledgers);

  // Get common data for dropdown options
  const commonData = useSelector(
    (state) => state.settlementServiceDashboard.commons.data
  );

  // Local State Management
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [filterParams, setFilterParams] = useState(filters || {});
  const [searchParams, setSearchParams] = useState({});

  const debouncedFilterParams = useDebounce(filterParams, 500);
  const debouncedSearchValue = useDebounce(searchParams, 500);

  const {
    isOpen: isAddNewLedgerSettlementModalOpen,
    onClose: onAddNewLedgerSettlementModalClose,
    onOpen: onAddNewLedgerSettlementModalOpen
  } = useDisclosure();

  const STRING_CONSTANTS =
    SETTLEMENT_SERVICE_DASHBOARD_STRINGS.STRING_CONSTANTS[TAB_STATES.LEDGER];

  const fetchLedgerSettlements = useCallback(() => {
    dispatch(
      getAllLedgers({
        payload: reqData
      })
    );
  }, [dispatch, reqData]);

  // Consolidated useEffect for all data fetching
  useDidMountEffect(() => {
    fetchLedgerSettlements();
  }, [debouncedFilterParams, debouncedSearchValue, pageNumber]);

  // Handle Page Change
  const handlePageChange = useCallback(
    (page) => {
      const newPageNumber = Math.max(0, page - 1);
      dispatch(
        updateSettlementServiceLedgersReqData({
          ...reqData,
          pageNumber: newPageNumber
        })
      );
    },
    [dispatch, reqData]
  );

  const handleSearchChange = (search) => {
    setSearchParams((prev) => ({
      ...prev,
      searchKey: search.searchKey,
      searchValue: search.searchValue
    }));
    dispatch(
      updateSettlementServiceLedgersReqData({
        ...reqData,
        searchKey: search.searchKey,
        searchValue: search.searchValue,
        pageNumber: 0
      })
    );
  };

  const handleFilterChange = useCallback(
    ({ key, filters }) => {
      setFilterParams((prev) => {
        const updatedParams = { ...prev };
        if (filters.length === 0) {
          delete updatedParams[key];
        } else {
          updatedParams[key] = filters;
        }
        return updatedParams;
      });
      dispatch(
        updateSettlementServiceLedgersReqData({
          ...reqData,
          filters: {
            ...reqData.filters,
            [key]: filters
          },
          pageNumber: 0
        })
      );
    },
    [dispatch, reqData]
  );

  const handleAddNewAccountSettlementSubmit = (formData) => {
    setIsModalLoading(true);
    dispatch(
      config.addNewLedgerSettlement({
        payload: config.addLedgerSettlementPayloadConvertor({ formData }),
        onSuccess: () => {
          fetchLedgerSettlements();
        },
        onComplete: () => {
          setIsModalLoading(false);
        }
      })
    );
    onAddNewLedgerSettlementModalClose();
  };

  return (
    <Box h="100%" overflow="hidden">
      <ModalLayout
        isOpen={isAddNewLedgerSettlementModalOpen}
        onClose={onAddNewLedgerSettlementModalClose}
      >
        <DynamicInputModal
          title={STRING_CONSTANTS.INPUT_MODAL_TITLE}
          isOpen={isAddNewLedgerSettlementModalOpen}
          onClose={onAddNewLedgerSettlementModalClose}
          onSubmit={handleAddNewAccountSettlementSubmit}
          fields={config.newConfigFieldsConfig(commonData) || []}
          isLoading={isModalLoading}
        />
      </ModalLayout>
      <VStack
        h="100%"
        overflowY="auto"
        overflowX="hidden"
        alignItems="stretch"
        gap={4}
        px={4}
        pb={4}
        divider={<StackDivider borderColor="whiteAlpha.300" />}
      >
        <HStack justify={'space-between'} flexShrink={0}>
          <Heading size={'md'}> {STRING_CONSTANTS.HEADING} </Heading>
          <Button
            colorScheme={'brand'}
            size="sm"
            bg="backGround"
            border={'2px solid'}
            borderColor={'whiteAlpha.300'}
            borderRadius={'lg'}
            p={4}
            leftIcon={<MdAdd size={'1.5rem'} />}
            onClick={onAddNewLedgerSettlementModalOpen}
          >
            {STRING_CONSTANTS.ADD_NEW_LEDGER}
          </Button>
        </HStack>

        <VStack justifyContent={'stretch'} alignItems="start" gap={2}>
          {/*Search*/}
          {!!searches?.length && (
            <SearchBox
              searches={searches}
              handleSearchChange={handleSearchChange}
            />
          )}
          {filters && (
            <FilterSelect
              handleFilterSubmit={handleFilterChange}
              filtersData={filters}
              defaultFiltersData={SSD_LEDGERS_DEFAULT_FILTERS || {}}
            />
          )}
        </VStack>

        {/*Pagination*/}
        {totalPages > 1 && (
          <Pagination
            currentPage={pageNumber + 1}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}

        {/*Card Data*/}
        {isLoading ? (
          <Flex justifyContent="center" alignItems="center" w="full" h="full">
            <Spinner thickness="4px" size="lg" color="colorPrimary" />
          </Flex>
        ) : !!error ? (
          <Box
            p={4}
            borderRadius="xl"
            bg={'backGround'}
            borderWidth="1px"
            borderColor={'whiteAlpha.300'}
          >
            <Text color="red.500" fontSize="md">
              Error Fetching Ledger Settlements: {error}
            </Text>
          </Box>
        ) : ledgerSettlements?.length ? (
          <VStack alignItems="start" gap={4} flex={1} w="full">
            {ledgerSettlements.map((ledgerSettlement, index) => (
              <GenericCard
                key={ledgerSettlement.settlementId || index}
                cardData={ledgerSettlement}
                cardType={CARD_TYPE.SETTLEMENT_SERVICE_DASHBOARD.LEDGER_CARD}
                maxW={'100%'}
                selected={
                  selectedLedger?.settlementId ===
                  ledgerSettlement?.settlementId
                }
              />
            ))}
          </VStack>
        ) : (
          <Box
            p={4}
            borderRadius="xl"
            bg={'backGround'}
            borderWidth="1px"
            borderColor={'whiteAlpha.300'}
          >
            <Text color="white" fontSize="md">
              No Ledger Settlements Found!
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default LedgerSettlements;
